import type { ReactElement } from "react";
import cx from "classnames";
import Link from "next/link";
import Image from "@olivahealth/web-client/src/components/common/Image";
import { APP_STORE_LINK, PLAY_STORE_LINK } from "@olivahealth/constants";
import Text from "../../atoms/Text";
import * as s from "./styles";

type DownloadBannerVariant = "horizontal" | "vertical";

interface PropsCommon {
  imageAlt?: string;
  title: string;
  qrCodeEl: ReactElement;
  headerIcon?: ReactElement;
  containerStyle?: string;
  hideAppleLink?: boolean;
  hideAndroidLink?: boolean;
  theme?: "light" | "dark";
}

type Props =
  | (PropsCommon & {
      variant?: Extract<DownloadBannerVariant, "horizontal">;
      imagePath: string;
    })
  | (PropsCommon & {
      variant?: Extract<DownloadBannerVariant, "vertical">;
      imagePath?: string;
    });

export default function AlertBanner({
  variant = "horizontal",
  imagePath,
  imageAlt,
  title,
  qrCodeEl,
  headerIcon,
  containerStyle,
  hideAppleLink = false,
  hideAndroidLink = false,
  theme = "light",
}: Readonly<Props>): ReactElement | null {
  return variant === "horizontal" ? (
    <div
      className={cx(s.contentWrapper, {
        "border border-neutral-200": theme === "light",
        "border border-neutral-600 bg-neutral-800": theme === "dark",
      })}
    >
      <div className={s.imageWrapper}>
        <Image
          className="object-contain md:absolute md:bottom-0"
          src={imagePath as string}
          alt={imageAlt ?? ""}
          sizes="(max-width: 768px) 80px, 100px"
          width={100}
          height={155}
        />
      </div>

      <div className={s.textWrapper}>
        <Text weight="bold" color={theme === "dark" ? "white" : undefined}>
          {title}
        </Text>
        <div className={s.linksWrapper}>
          <span className="order-2 md:order-1">{qrCodeEl}</span>
          {(!hideAndroidLink || !hideAppleLink) && (
            <div className={s.linksInnerWrapper}>
              {!hideAppleLink && (
                <Link href={APP_STORE_LINK} target="_blank">
                  <Image
                    className="object-contain"
                    src="/img/download-app-store.png"
                    alt="Download on App Store"
                    sizes="(max-width: 768px) 90px, 108px"
                    width={108}
                    height={32}
                  />
                </Link>
              )}
              {!hideAndroidLink && (
                <Link href={PLAY_STORE_LINK} target="_blank">
                  <Image
                    className="object-contain"
                    src="/img/download-google-play.png"
                    alt="Download on Play Store"
                    sizes="(max-width: 768px) 90px, 108px"
                    width={108}
                    height={32}
                  />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div
      className={cx(
        "relative p-4 flex flex-col gap-4 items-center rounded-xl",
        {
          "bg-neutral-50": theme === "light",
          "bg-neutral-800": theme === "dark",
        },
        containerStyle,
      )}
    >
      {headerIcon && (
        <div className="absolute -top-0 left-3 h-[72px] w-[72px] -translate-y-1/2">
          {headerIcon}
        </div>
      )}
      <div
        className={cx("flex gap-4 items-center", {
          "mt-6": headerIcon,
        })}
      >
        <Text
          fontFamily="sans"
          size="sm"
          weight="medium"
          color={theme === "dark" ? "white" : undefined}
        >
          {title}
        </Text>
        <span className="shrink-0 ml-2">{qrCodeEl}</span>
      </div>
    </div>
  );
}
